import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export function scrollToId(id) {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -10, duration: 1.3 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export function Section({ id, className = "", children }) {
  return (
    <section id={id} className={`relative w-full ${className}`}>
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:px-24">{children}</div>
    </section>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-[6px] w-[6px] rounded-full bg-brand" />
      <span className="eyebrow">{children}</span>
    </div>
  );
}

export function CtaButton({
  children,
  to = "#looped-in",
  variant = "primary",
  testid,
  className = "",
  onClick,
}) {
  const base =
    "group relative inline-flex items-center gap-3 rounded-full py-2 pl-6 pr-2 font-display text-sm font-semibold tracking-tight transition-all duration-300 will-change-transform hover:-translate-y-[2px]";
  const styles =
    variant === "primary"
      ? "bg-[#111318] text-cream dark:bg-brand dark:text-ink"
      : "border border-line text-content";
  const badge =
    variant === "primary"
      ? "bg-brand text-ink dark:bg-ink dark:text-brand"
      : "bg-content text-page";

  const inner = (
    <>
      <span>{children}</span>
      <span className={`grid h-9 w-9 place-items-center rounded-full transition-transform duration-300 group-hover:rotate-45 ${badge}`}>
        <ArrowUpRight size={16} strokeWidth={2.4} />
      </span>
    </>
  );

  if (to && to.startsWith("#")) {
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={(e) => {
          onClick?.(e);
          scrollToId(to);
        }}
        className={`${base} ${styles} ${className}`}
      >
        {inner}
      </button>
    );
  }
  if (to && (to.startsWith("/") || to.startsWith("http"))) {
    return (
      <Link to={to} data-testid={testid} className={`${base} ${styles} ${className}`} onClick={onClick}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" data-testid={testid} onClick={onClick} className={`${base} ${styles} ${className}`}>
      {inner}
    </button>
  );
}

// Cycles through words with a vertical flip. Used in both tickers.
export function WordCycle({ words, interval = 2200, className = "", accent = true }) {
  const [i, setI] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words.length, interval]);

  return (
    <span
      ref={ref}
      className={`relative inline-grid overflow-hidden align-bottom ${className}`}
      style={{ verticalAlign: "bottom" }}
    >
      {/* sizer keeps width to the longest word to avoid layout shift */}
      <span className="invisible col-start-1 row-start-1 whitespace-nowrap" aria-hidden="true">
        {words.reduce((a, b) => (a.length >= b.length ? a : b), "")}
      </span>
      <AnimatePresence mode="wait">
        <motion.span
          key={words[i]}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          exit={{ y: "-110%" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className={`col-start-1 row-start-1 justify-self-start whitespace-nowrap font-display ${accent ? "emph" : "text-content"}`}
        >
          {words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
